<template>
  <Content
    class="manifest-detail"
    :loading="manifest.loading"
    :back-btn-route="{ name: 'manifestList', query: { search: backSearch } }"
    :back-btn-label="t('manifests.backToAllManifests')"
    remove-card-padding
    remove-aside-card-padding
  >
    <template #headerLeft>
      <CarrierAvatar
        v-if="manifest.data?.carrier?.scac"
        :carrier="avatarCarrier"
        size="large"
      />
      <HeaderIcon v-else icon="file-alt" />
      <h1 id="content-heading" tabindex="-1">
        {{ manifest.data?.manifestNumber ?? t('manifests.manifests', 1) }}
      </h1>
      <ManifestStatusChip
        v-if="manifest.data?.status"
        :status="manifest.data.status"
      />
    </template>

    <template #headerRight>
      <ManifestActionButton
        v-model="manifest"
        @print="onPrint"
        @cancel="onCancel"
      />
    </template>

    <Section
      id="manifest-summary"
      :header="t('common.basicInfo')"
      class="manifest-basic-info-section p-0"
      data-class="manifest-basic-info-section-data"
    >
      <Message
        v-if="manifest.data?.errors.length"
        severity="error"
        variant="outlined"
      >
        <h2 class="manifest-error-header">
          {{ t('manifests.manifestErrors', manifest.data.errors.length) }}
        </h2>

        <ul>
          <li
            v-for="(error, i) in manifest.data.errors"
            :key="i"
            class="rygen-message"
          >
            {{ error }}
          </li>
        </ul>
      </Message>
      <div class="row">
        <div
          v-for="row in summaryRows"
          :key="row.key"
          class="manifest-basic-info-cell"
        >
          <KeyValuePair :label="row.label" :value="row.value" uppercase-label>
            <template v-if="row.key === 'carrier'" #value>
              <CarrierAvatarAndName
                :name="manifest.data?.carrier?.name"
                :scac="manifest.data?.carrier?.scac"
              />
            </template>
          </KeyValuePair>
        </div>
      </div>
    </Section>

    <Section id="manifest-shipments" :header="t('shipments.shipments', 2)">
      <ShipmentSearchResultView
        v-model:columns="shipmentColumns"
        :shipment-search-criteria="shipmentCriteria"
        :results="shipments"
        readonly
        @page="onShipmentPage"
        @sort="onShipmentSort"
      />
    </Section>
  </Content>
</template>

<script lang="ts" setup>
import { CancelTokenSource, default as axios } from 'axios';
import { useConfirm } from 'primevue/useconfirm';
import type { Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { useToast } from '@rygen/rygen-ui/composables';
import { translateEnum } from '@rygen/rygen-ui/i18n';
import { ColumnSpecification, Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  Carrier,
  Manifest,
  REQUESTED_PICKUP_DATE_RANGE_COLUMN,
  SERVICE_COLUMN,
  ShipmentSearchCriteria,
  ShipmentSearchResult,
  WEIGHT_COLUMN,
} from '@/model';
import { useCarrierStore } from '@/stores/Carrier';

import CarrierAvatarAndName from '@/components/carrier/CarrierAvatarAndName.vue';
import ManifestActionButton from '@/components/manifest/ManifestActionButton.vue';
import ManifestStatusChip from '@/components/manifest/ManifestStatusChip.vue';
import ShipmentSearchResultView from '@/components/shipment/ShipmentSearchResult.vue';
import CarrierAvatar from '@rygen/rygen-ui/components/carrier/CarrierAvatar.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import HeaderIcon from '@rygen/rygen-ui/components/content/HeaderIcon.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';
import Message from 'primevue/message';

const { loadCarrierByScac } = useCarrierStore();
const confirm = useConfirm();
const { t, d } = useI18n();
const route = useRoute();
const toast = useToast();

let manifestCancel: CancelTokenSource | undefined;
let shipmentsCancel: CancelTokenSource | undefined;

const carrierLoadable: Ref<Loadable<Carrier>> = ref({ loading: false });
const manifest: Ref<Loadable<Manifest>> = ref({ loading: false });
const shipments: Ref<Loadable<ShipmentSearchResult>> = ref({
  loading: false,
  data: new ShipmentSearchResult(),
});
const shipmentCriteria = ref(
  new ShipmentSearchCriteria({ page: 0, pageSize: 20 }),
);
const shipmentColumns: Ref<ColumnSpecification[]> = ref([
  REQUESTED_PICKUP_DATE_RANGE_COLUMN,
  SERVICE_COLUMN,
  WEIGHT_COLUMN,
]);

const manifestId = computed(() => Number(route.params.manifestId));
const backSearch = computed(() => route.query.search as string | undefined);

const avatarCarrier = computed(
  () =>
    new Carrier({
      name: manifest.value.data?.carrier?.name,
      scac: manifest.value.data?.carrier?.scac,
      ...carrierLoadable.value.data,
    }),
);

const locationLabel = computed(() => {
  const location = manifest.value.data?.pickupLocation;
  if (!location) {
    return '-';
  }
  const name = location.locationName ?? location.code ?? '';
  const region = [location.city, location.state].filter(Boolean).join(', ');
  return [name, region].filter(Boolean).join(' — ') || '-';
});

const summaryRows = computed(() => {
  const data = manifest.value.data;
  return [
    {
      key: 'manifestNumber',
      label: t('manifests.manifestNumber'),
      value: data?.manifestNumber ?? '-',
    },
    {
      key: 'client',
      label: t('clients.clients'),
      value: data?.domain?.name ?? '-',
    },
    { key: 'carrier', label: t('carriers.carriers'), value: '' },
    {
      key: 'location',
      label: t('locations.locations'),
      value: locationLabel.value,
    },
    {
      key: 'manifestDate',
      label: t('common.paramDates', {
        param: t('manifests.manifests'),
        count: 1,
      }),
      value: data?.manifestDay
        ? d(data.manifestDay.toJSDate(), 'numeric')
        : '-',
    },
    {
      key: 'shipments',
      label: t('shipments.shipments', 2),
      value: `${data?.shipmentIds?.length ?? 0}`,
    },
    {
      key: 'source',
      label: t('common.source'),
      value: data?.source ? translateEnum(data.source, 'ManifestSource') : '-',
    },
  ];
});

const loadManifest = async () => {
  try {
    manifest.value.loading = true;
    manifestCancel?.cancel();
    manifestCancel = axios.CancelToken.source();
    manifest.value.data = await api.shipmentManifest.getManifest(
      manifestId.value,
      manifestCancel,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }
    manifest.value.error = e;
    console.error('Error loading manifest', e);
    toast.addError({
      detail: t('errors.loadingParam', { param: t('manifests.manifests', 1) }),
      error: e,
    });
  } finally {
    manifest.value.loading = false;
  }
};

const loadShipments = async () => {
  try {
    shipments.value.loading = true;
    shipmentsCancel?.cancel();
    shipmentsCancel = axios.CancelToken.source();
    shipments.value.data = await api.shipmentManifest.getManifestShipments(
      manifestId.value,
      {
        page: shipmentCriteria.value.page,
        pageSize: shipmentCriteria.value.pageSize,
        sortField: shipmentCriteria.value.sortField as string | undefined,
        sortDirection: shipmentCriteria.value.sortDirection,
      },
      shipmentsCancel,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }
    console.error('Error loading manifest shipments', e);
    toast.addError({
      detail: t('errors.loadingParam', { param: t('shipments.shipments', 2) }),
      error: e,
    });
  } finally {
    shipments.value.loading = false;
  }
};

const onShipmentPage = (event: DataTablePageEvent) => {
  shipmentCriteria.value.pageSize = event.rows as number;
  shipmentCriteria.value.page = event.page as number;
  loadShipments();
};

const onShipmentSort = (event: DataTableSortEvent) => {
  shipmentCriteria.value.sortField =
    event.sortField as ShipmentSearchCriteria['sortField'];
  shipmentCriteria.value.sortOrder = (event.sortOrder ?? 0) as -1 | 0 | 1;
  loadShipments();
};

const onPrint = () => {
  // A manifest can span multiple documents (e.g. USPS SCAN forms cap at 500
  // shipments per PDF), so open every available document.
  const urls = (manifest.value.data?.documents ?? [])
    .map((doc) => doc.signedUrl)
    .filter((url): url is string => !!url);
  if (urls.length === 0) {
    toast.addWarning({ detail: t('manifests.documentNotReady') });
    return;
  }
  urls.forEach((url) => window.open(url, '_blank', 'noopener'));
};

const cancelManifest = async () => {
  try {
    await api.shipmentManifest.cancelManifest(manifestId.value);
    toast.addSuccess({
      detail: t('messages.successCancelingParam', {
        param: t('manifests.manifests', 1),
      }),
    });
    loadManifest();
  } catch (e) {
    console.error('Error canceling manifest', e);
    toast.addError({
      detail: t('errors.cancelingParam', {
        param: t('manifests.manifests', 1),
      }),
      error: e,
    });
  }
};

const onCancel = () => {
  const param = t('manifests.manifests', 1);
  confirm.require({
    group: 'rygen-confirm-dialog',
    header: t('common.cancelParam', { param }),
    message: t('common.confirmCancelationParams', { param }),
    acceptClass: 'p-button-danger p-button-sm',
    acceptLabel: t('common.cancelParam', { param }),
    accept: cancelManifest,
    rejectClass: 'p-button-text p-button-sm',
    rejectLabel: t('common.dontCancelParam', { param }),
  });
};

watch(
  () => manifest.value.data?.carrier?.scac,
  (scac) => {
    carrierLoadable.value = scac ? loadCarrierByScac(scac) : { loading: false };
  },
  { immediate: true },
);

watch(
  manifestId,
  () => {
    loadManifest();
    loadShipments();
  },
  { immediate: true },
);
</script>

<style>
.manifest-detail {
  .manifest-basic-info-section {
    --section-header-margin: var(--sp-6) var(--sp-6) var(--sp-3);

    .manifest-error-header {
      color: var(--p-message-error-outlined-color);
      padding-bottom: var(--sp-3);
    }

    .manifest-basic-info-section-data {
      padding: 0 var(--sp-3) var(--sp-3);

      .row {
        display: flex;
        flex-wrap: wrap;

        .manifest-basic-info-cell {
          padding: var(--sp-3);
          flex: 1 0 auto;
          width: 30%;
        }
      }
    }
  }
}
</style>
